import {
  Alert,
  AnalyticsEvent,
  AnalyticsEventNameType,
  AnalyticsEventPlatformType,
  BillingGatedActionType,
  Column,
  DataImport,
  DataImportOptions,
  DataImportSchedule,
  DataObjectField,
  DataObjectType,
  DataSource,
  DataSourceType,
  DefaultApi,
  DeleteUser200Response,
  DueInvoiceErrorType,
  FileIdentifier,
  Filter,
  FlatColumnConfig,
  FlatColumnType,
  GatedFeature,
  GetClientFlags200Response,
  GetDataImports200Response,
  HubspotObjectTypeField,
  HubspotObjectTypeFieldType,
  DataImportCreationSource,
  PPressFormMode,
  PpressRenderForm200Response,
  PreviewColumn,
  RunDataImport200Response,
  RunWriteback200Response,
  SheetRangeColumn,
  SignupSourceType,
  Sort,
  SubscriptionPlanType,
  Writeback,
  WritebackMapping,
  WritebackMappingLabel,
  FilterOperatorType,
  AlertType,
  PivotPreviewMetadata,
  RunDataImportPreviewRun200ResponsePreviewDataRowsInnerInner,
  RunDataImportPreviewRun200Response,
  DataImportStatus,
  FilterClause,
} from "@coeff/api";
import React, { ReactNode } from "react";

import { WindowMetadata } from "../globals";
import type { AlertStateTrixRecord } from "../server/trix-db";
import {
  AugmentedDataImport,
  BuildFlags,
  CoeffGasUserProperties,
  CoeffTemplateInfo,
  DataImportFile,
  DataImportFiles,
  PlatformAttributes,
  RunDataImportOptions,
  SQLParameter,
  Tab,
  TabInfo,
} from "../shared";
import type { ImportModalDialogProps } from "../shared/dialogs";

import { AxiosErrorWithReason } from "./api";
import { ErrorMessageProps } from "./components/ErrorMessage";
import { OnApplyTemplateCb } from "./components/TemplatesPopover/TemplatesPopover";
import { CoeffGasFunctions } from "./server-interface";
import { APIError, DataImportRecipeNavigationProps, ResolvedWritebackDataSource } from "./utils";
import { CoeffRouter } from "./utils/router";
import { RootState } from "./views/Root";
import type { WritebackTemplateFlow } from "./views/WritebackDetails/templates/types";
import { WritebackSchema } from "./views/WritebackDetails/WritebackDetails";

export interface BoxFile {
  id: string;
  type: "file" | "folder";
  name: string;
  size?: number;
  parent?: {
    id: string;
    name: string;
  };
  modified_at?: string;
  created_at?: string;
}

export interface BoxPickerOptions {
  container?: string;
  maxSelectable?: number;
  canSetShareAccess?: boolean;
  canCreateNewFolder?: boolean;
  canUpload?: boolean;
  extensions?: string[];
  logoUrl?: string;
}

export interface BoxContentPicker {
  show(folderId: string, token: string, options: BoxPickerOptions): void;
  hide(): void;
  addListener(event: "choose", callback: (items: BoxFile[]) => void): void;
  addListener(event: "cancel", callback: () => void): void;
}

export interface BoxFilesResponse {
  files: Array<{
    id: string;
    name: string;
    type: string;
    url: string;
  }>;
  type?: string;
}

export interface EgnyteFilesResponse {
  files: Array<{
    name: string;
    path: string;
    url: string;
  }>;
  type?: string;
}

declare global {
  interface Window {
    analytics: Analytics;
    Box: {
      ContentPicker: new () => BoxContentPicker;
    };
  }

  // Definitions based off the Segment SDK
  interface Analytics {
    identify: (
      userId?: string,
      properties?: object,
      options?: object,
      callback?: () => void
    ) => void;
    page: (category?: string, properties?: object, options?: object, callback?: () => void) => void;
    track: (event: string, properties?: object, options?: object, callback?: () => void) => void;
  }
}
export interface BroadcastConstructor {
  adminCSVImport: BroadcastChannel;
  billingChannel: BroadcastChannel;
  fileChannel: BroadcastChannel;
  lookerChannel: BroadcastChannel;
  tableauChannel: BroadcastChannel;
  importPreviewChannel: BroadcastChannel;
  writebackFieldMappingsChannel: BroadcastChannel;
  writebackPreviewChannel: BroadcastChannel;
  alertChannel: BroadcastChannel;
  browserAgentChatChannel: BroadcastChannel;
  smartAlertPreviewChannel: BroadcastChannel;
}

export { BuildFlags };

export type {
  AugmentedDataImport,
  CoeffRouter,
  DataImportFile,
  DataImportFiles,
  SQLParameter,
  Tab,
  TabInfo,
};

// Egnyte-specific file type that extends DataImportFile with a path property
export interface EgnyteDataImportFile extends DataImportFile {
  path: string;
}

export enum PPressFormType {
  IMPORT_DETAIL = "IMPORT_DETAIL",
  SIDEPANE = "SIDEPANE",
  TOOLBAR = "TOOLBAR",
  PREVIEW_PLACEHOLDER = "PREVIEW_PLACEHOLDER",
  CUSTOM_FULL_SCREEN = "CUSTOM_FULL_SCREEN",
  PRIMARY_CONTENT = "PRIMARY_CONTENT",
  TEMPLATE_SETUP = "TEMPLATE_SETUP",
}

export type AlertRangeType = "range" | "sheet";

export type SheetsOfCurrentSpreadsheet = {
  name: string;
  sheetA1Notation: string;
  isActive: boolean;
  sheetId: number;
}[];

export interface AlertContext {
  syncedAlert: Alert | undefined;
  setSyncedAlert: (alert: Alert) => void;
  currentAlert: Alert;
  errorStates: AlertErrorStateType;
  onNameChange: (name: string) => void;
  onTriggerConditionTypeChange: (value: AlertType) => void;
  onTriggerConditionChange: (value: FilterOperatorType) => void;
  onTriggerValueChange: (event: any) => void;
  setCurrentAlert: (value: React.SetStateAction<Alert>) => void;
  setDeleteConfirm: (val: boolean) => void;
  selectedTableColumnHeaders: SheetRangeColumn[];
  setSelectedTableColumnHeaders: (value: SheetRangeColumn[]) => void;
  sheetsOfCurrentSpreadsheet: SheetsOfCurrentSpreadsheet;
}

export interface AlertErrorStateType {
  name: boolean;
  cellRange: boolean;
  value: boolean;
  emailAddresses: boolean;
  slackChannel: boolean;
  idColumn: boolean;
  dynamicRecipients: boolean;
}

export enum APIErrorType {
  APIError = "APIError",
  NotInGsuiteDomainWhitelist = "NotInGsuiteDomainWhitelist",
}

export interface WritebackError {
  message?: React.ReactNode;
  title?: React.ReactNode;
  originalError?: unknown;
  level?: "error" | "warning";
  action?: () => void;
  actionText?: string;
}

export type DataImportUIState = "running" | "saving";

export type WritebackUIState = "running" | "completed" | "failed";

export interface DMContext {
  alerts: Alert[];
  alertStateTrixRecords: AlertStateTrixRecord[];
  api: DefaultApi;
  createAlert: (alert: Alert) => Promise<Alert>;
  createDataImport: (
    dataImport: DataImport,
    event_props: EventProperties,
    data_import_creation_source: DataImportCreationSource
    // Returns [dataImport, isFirstDataImport, isFirstCoreDataImport]. The third flag is the
    // activation signal the Meta "first_data_import" attribution event triggers on (ENG-8386).
  ) => Promise<[DataImport, boolean, boolean]>;
  createWriteback: (writeback: Writeback) => Promise<Writeback>;
  dataImportUIStateById: {
    [dataImportId: string]: DataImportUIState | undefined;
  };
  dataImports: DataImport[];
  deleteAlert: (alertId: string) => Promise<DeleteUser200Response>;
  deleteDataSource: (dataSource: DataSource) => Promise<DataSource | any>;
  deleteWriteback: (writeback: Writeback) => Promise<DeleteUser200Response>;
  ephemeralImportErrorByImportId: { [dataImportId: string]: ErrorMessageProps };
  deleteDataImport: (dataImport: DataImport) => Promise<false | DeleteUser200Response>;
  fetchAlerts: () => Promise<Alert[]>;
  fetchDataImports: () => Promise<GetDataImports200Response>;
  fetchDataSourceById: (id: string) => Promise<DataSource>;
  fetchWritebacks: () => Promise<Writeback[] | APIError>;
  removeEphemeralImportError: (dataImportid: string | undefined) => void;
  setDataImportUIStateById: (id: string, state: DataImportUIState | undefined) => void;
  runDataImport: (
    dataImport: AugmentedDataImport,
    options: Omit<RunDataImportOptions, "analytics_props"> & {
      disableReauthHandling?: boolean;
      // Bulk poll is on by default; pass false to fall back to per-import polling.
      enableBulkImportRunPoll?: boolean;
    }
  ) => Promise<RunDataImport200Response>;
  runWriteback: (
    writeback: Writeback,
    selectedRowsIndices?: number[],
    previewRunId?: string,
    dataSourceId?: string
  ) => Promise<RunWriteback200Response>;
  setEphemeralImportError: (
    dataImportId: string | undefined,
    importError: ErrorMessageProps
  ) => void;
  setWritebackUIStateById: (writebackId: string, state: WritebackUIState | undefined) => void;
  writebackUIStateById: {
    [writebackId: string]: WritebackUIState | undefined;
  };
  updateAlert: (alert: Alert) => Promise<Alert>;
  updateDataImport: (
    dataImport: DataImport,
    options: { handlePivotToggle: boolean; skipUIUpdate?: boolean }
  ) => Promise<DataImport>;
  updateDataImportTargetInfo: (
    dataImport: DataImport,
    arg: { tab_name?: string; maybe_target_tab_deleted?: boolean }
  ) => Promise<void>;
  updateDataSource: (dataSource: DataSource) => Promise<DataSource | APIError>;
  updateWriteback: (writeback: Writeback) => Promise<Writeback>;
  updateWritebackMappingLabels: (
    writebackId: string,
    labels: WritebackMappingLabel[]
  ) => Promise<WritebackMapping[]>;
  writebacks: Writeback[];
}

export interface EventProperties {
  addon_build_dt?: string;
  addon_version?: string;
  api_endpoint?: string;
  domain?: string;
  email?: string;
  first_name?: string;
  gas_install_source?: string;
  last_name?: string;
  platform?: AnalyticsEventPlatformType;
  user_agent?: string;
  user_locale?: string;
  user_id?: string;
  ss_num_alerts?: number;
  ss_num_imports?: number;
  ss_num_tabs?: number;
  ss_num_writebacks?: number;
  ss_num_formulas?: number;
  ss_num_pbdashes?: number;
  ss_num_shared_with?: number;
  ss_id?: string;
  // Excel needs more than id to identify the file.
  // Going forward we can use ss_identifier.
  ss_identifier?: FileIdentifier;
  ss_name?: string;
  ss_orig_name?: string;
  ss_client_key?: string;
  ss_locale?: string;
  ss_tpl_name?: string;
  template_name?: string;
}

export interface EMContext {
  // Returns the /api/a/t POST promise (ENG-8602 uses it to roll back the paywall-
  // shown throttle on failure). `void` is kept in the union so providers that don't
  // wire a real POST still satisfy the type.
  track: (
    event: AnalyticsEventNameType,
    extraProperties?: AnalyticsEvent
  ) => Promise<unknown> | void;
}

export enum PreviewModalTrigger {
  AUTO = "auto",
  FIELD_SELECTOR_EDIT = "fieldSelectorEdit",
  CUSTOM_SQL_EDIT = "customSQLEdit",
  FILTER_AND_SORT_EDIT = "filterAndSortEdit",
  ORGANIZATION_EDIT = "organizationEdit",
  ENDPOINT_EDIT = "endpointEdit",
  FIELD_SELECTOR_BUTTON = "fieldSelectorButton",
  LIMIT_EDIT = "limitEdit",
  DATES_EDIT = "datesEdit",
  PREVIEW_BUTTON = "previewButton",
  MISSING_COLUMNS_LINK = "missingColumnsLink",
  SUMMARY_PROMPT_EDIT = "summaryPromptEdit",
  QUERY_STRING_EDIT = "queryStringEdit",
  DISABLED_PREVIEW_FOR_NON_OWNERS = "disabledPreviewForNonOwners",
}

export interface IDContext {
  reauthed?: boolean;
  setShowAutoAnalystPopup: (args: boolean) => void;
  displayAutoAnalyze: boolean;
  analyzeImport: (intent?: string) => Promise<void>;
  isAnalyzeImportLoading: boolean;
  isAutoOpenPreviewModal?: boolean;
  currentPreviewModalTrigger: string | undefined;
  isImportFromDataImportRecipe: boolean;
  isEditingDataImportRecipe: boolean;
  dataImportRecipeId?: string;
  currentDataImport: AugmentedDataImport;
  isCreatingAndRunningDataImport: boolean;
  currentDataSource?: DataSource;
  dataImportWritebacks: Writeback[];
  hasUserChangedName: boolean;
  filterErrors: number[];
  handleDelete: (arg: boolean) => void;
  isDuplicateImport: boolean;
  isCTAHidden: boolean;
  openImportDialog: (props?: ImportModalDialogProps) => void;
  openPreviewModal: (trigger: string, props?: PreviewModalProps) => void;
  openPermissionsModal: (isOpen: boolean) => void;
  removeSchedule: () => void;
  removeSnapshotSchedule: () => void;
  setCurrentDataImport: (
    currentDataImport:
      | AugmentedDataImport
      | ((currentDataImport: AugmentedDataImport) => AugmentedDataImport)
  ) => void;
  setPartialPPressParams: (ppressParams: PPressParams) => void;
  setIsCTAHidden: (value: boolean) => void;
  setIsCTADisabled: (value: boolean) => void;
  shouldAutoOpenFieldSelector: boolean;
  setShouldAutoOpenFieldSelector: (value: boolean) => void;
  setFilterErrors: (errors: number[]) => void;
  setFilters: (filters: Filter[]) => void;
  setName: (name: string) => void;
  setSchedule: (schedule: DataImportSchedule) => void;
  setSnapshotSchedule: (snapshot_schedule: DataImportSchedule) => void;
  setOptions: (options: DataImportOptions) => void;
  setSorts: (sorts: Sort[]) => void;
  setColumns: (columns: Column[]) => void;
  toggleColumn: (index: number) => void;
  editAccessMessageVisible: boolean;
  setEditAccessMessageVisible: (args: boolean) => void;
  ppressFormState: PPressFormState;
  ppressRenderForm: (
    ppressParams?: PPressParams,
    options?: {
      validate?: boolean;
      shouldResetDataImport?: boolean;
    }
  ) => Promise<PpressRenderForm200Response | undefined>;
  showImportCountBasedRecipePromoteMessage: boolean;
  importSaved: boolean;
  handleRun: (
    dataImport: DataImport,
    options: Omit<RunDataImportOptions, "analytics_props">
  ) => void;
  syncedDataImport?: DataImport;
  setCtaOverride: (override: { onClick: () => Promise<void>; label?: string } | undefined) => void;
  setIsUpdatingAndRunning: (value: boolean) => void;
}

export interface IPContext {
  availableColumns: PreviewColumn[];
  missingColumns: PreviewColumn[];
  dataImport: AugmentedDataImport;
  dataSource?: DataSource;
  flatColumns: PreviewColumn[];
  filteredColumns: PreviewColumn[];
  columns: PreviewColumn[];
  selectedColumns: string[];
  rows: any[][];
  loading: boolean;
  isLongFetchPreview?: boolean;
  isFetchingPreviewColumns?: boolean;
  fetchingPreview: boolean;
  highlightColumn?: string;
  previewCanceled: boolean;
  setPreviewCanceled: (q: boolean) => void;
  fetchPreviewData: (params: {
    dataImport: DataImport;
    triggeredBy: "automatic" | "manual_click";
    ppressParams?: Record<string, any>;
  }) => Promise<RunDataImportPreviewRun200Response | undefined>;
  fetchPreviewColumns: (
    dataImport: DataImport,
    ppressParams?: Record<string, any>
  ) => Promise<RunDataImportPreviewRun200Response | undefined>;
  isApplyingTemplate: boolean;
  displayTemplatesPopover: boolean;
  onApplyTemplate: OnApplyTemplateCb;
  onStartWithTemplate: () => void;
  previewError?: unknown;
  previewLastImportStatus?: DataImportStatus | undefined;
  popoverColumn?: PreviewColumn;
  popoverColumnData: any[];
  data: object[];
  pivotPreviewData: Array<Array<RunDataImportPreviewRun200ResponsePreviewDataRowsInnerInner>>;
  pivotPreviewMetadata: PivotPreviewMetadata | undefined;
  metadata: WindowMetadata;
  query: string;
  setColumns: (arg: PreviewColumn[]) => void;
  setFlatColumns: (arg: PreviewColumn[]) => void;
  setLoading: (arg: boolean) => void;
  setQuery: (q: string) => void;
  setDataImport: (
    arg: AugmentedDataImport | ((di: AugmentedDataImport) => AugmentedDataImport)
  ) => void;
  setMetadata: (arg: WindowMetadata) => void;
  setScrollToRight: () => void;
  setScrollToTop: () => void;
  selectAll: (e: any) => void;
  onColumnHover: (col?: PreviewColumn) => void;
  getPreviewTableSelectionInfo: () => {
    previewTableFooterText?: string;
    fieldSelectionInfoText?: string;
  };
  onDone: (dataImport: DataImport) => void;
  previewModalProps?: PreviewModalProps;
  isImportFromDataImportRecipe?: boolean;
  isEditingDataImportRecipe?: boolean;
  dataImportRecipeId?: string;
  openWithGPT?: boolean;
  clientFlags?: GetClientFlags200Response;
  // SQL decoupled loading states
  isFetchingSidepaneData?: boolean;
  isFetchingPreviewData?: boolean;
  isLongFetchPreviewData?: boolean;
  fetchSQLSidepaneData: (
    dataImport: DataImport
  ) => Promise<RunDataImportPreviewRun200Response | undefined>;
  fetchSQLPreviewData: (params: {
    dataImport: DataImport;
    triggeredBy: "automatic" | "manual_click";
    tab?: "pivot" | "table";
  }) => Promise<RunDataImportPreviewRun200Response | undefined>;
  hasPreviewDataBeenFetched: { pivot: boolean; table: boolean };
  setHasPreviewDataBeenFetched: React.Dispatch<
    React.SetStateAction<{ pivot: boolean; table: boolean }>
  >;
  cancelApiCalls: (params: { sidepane: boolean; preview: boolean }) => void;
}

export interface IPPContext extends IPContext {
  formState: PPressFormState;
}

export interface GSContext {
  gasServer: CoeffGasFunctions;
}

export type OpenBillingPageFrom =
  | "hamburger"
  | "payment_failed_message"
  | "last_run_status_display"
  | "scheduler_upgrade_to_pro"
  | "upgrade_to_pro_popup"
  | "upgrade_to_enterprise_popup"
  | "home_screen"
  | "snapshot_upgrade_to_pro"
  | "pro_trial_ended_popup"
  | "import_run_limit_reached_popup"
  | "ai_usage_limit_reached"
  | "webhook_upgrade_to_pro"
  // ENG-9319: "Or, purchase your own plan" on a Request License gate. The webapp shows the plan
  // chooser instead of its Request License page for it.
  | "purchase_own_plan";

export interface RContext {
  Broadcast: BroadcastConstructor;
  router: CoeffRouter;
  changeView: (newView: string | -1, options?: { props: object; replace?: boolean }) => void;
  redirectOnOpen?: boolean;
  redirectDataImportId?: string;
  redirectDataImportShowUsageWarning?: boolean;
  redirectDataImportTabId?: number;
  runDataImportTabId?: number;
  dataImportIdBySheetId: { [x: number]: string };
  dataImportIdByExcelSheetId: { [x: string]: string };
  eligible_for_help_video_drawer?: boolean;
  should_show_invite_coworker_banner?: boolean;
  should_show_salesforce_feedback?: boolean;
  should_show_alert_discovery_banner?: boolean;
  should_show_agentic_workflows_welcome?: boolean;
  should_show_browser_agent_onboarding?: boolean;
  free_trial_expiration_dt: Date | null;
  force_show_days_left_banner: boolean;
  free_trial_total_days: number | null;
  gas_user_props: CoeffGasUserProperties;
  isGsuiteDomainAdmin: boolean;
  event_props: EventProperties & AnalyticsEvent;
  metadataLoaded: boolean;
  coeff_tpl_info: CoeffTemplateInfo;
  openBillingPage: (from: OpenBillingPageFrom) => void;
  planType?: SubscriptionPlanType;
  isNewUser: boolean;
  setClientFlags: (clientFlags: Partial<RootState>) => void;
  initClientFlags: (fileId: string) => Promise<GetClientFlags200Response | object>;
  setGasUserProps: (gas_user_props: CoeffGasUserProperties) => void;
  setFreeTrial: (free_trial_expiration_dt: Date, free_trial_total_days: number) => void;
  setPlanType: (arg: SubscriptionPlanType) => void;
  setShowRatingPopup: (should_show_rating_popup: boolean) => void;
  should_show_in_app_templates?: boolean;
  should_show_rating_popup?: boolean;
  showUpgradePrompt: (showUpgradeMessage?: string) => void;
  showUpgradeToEnterprisePrompt: (dataSourceType?: DataSourceType, dataImportId?: string) => void;
  showImportAccessRestrictedPrompt: (options?: {
    event_from?: string;
    data_source?: DataSourceType;
    blocked_action?: BillingGatedActionType;
  }) => void;
  showContactSalesPrompt: () => void;
  ssScopedTauk: string | null;
  ssTabsInfo: TabInfo[];
  signup_source?: SignupSourceType;
  disallowed_features: GatedFeature[];
  due_invoices_count: number;
  due_invoice_error_type?: DueInvoiceErrorType;
  domain_blacklisted_for_billing?: boolean;
  writeback_disabled_for_user?: boolean;
  onboardingSurveySelectedDataSources?: DataSourceType[];
  onboardingSurveyDepartment?: string;
  locked_imports_enabled_for_user: boolean;
  num_imports_created?: number;
  showCloneConfirmOnOpen?: string;
  showChatOnOpen?: boolean;
  runAllDataImportsOnOpen?: boolean;
  isRunningAllDataImports?: boolean;
  showUnpauseSchedulesBanner?: boolean;
  unpauseSchedulesBannerOptions?: { duplicatedCount: number; totalCount: number };
  clearShowChatOnOpen: () => void;
  setShowChatOnOpen: (showChatOnOpen: boolean) => void;
  clearRedirectOnOpen: () => void;
  clearShowCloneConfirmOnOpen: () => void;
  clearOnsheetDataImportIdsUnableToRefresh: () => void;
  availableConnectors: {
    key: DataSourceType;
    name: string;
    isSupported: boolean;
    isDisabledByAdmin: boolean;
  }[];
  domains_in_domain_family: string[];
  asyncRefreshEnabled: boolean;
  asyncRefreshBackgroundAfterSecs: number;
  user_slack_token_unavailable: boolean;
  setUserSlackTokenUnavailable: (user_slack_token_unavailable: boolean) => void;
  clientFlags: GetClientFlags200Response;
  firstAddonPollingFinished: boolean;
  onsheetDataImportIdsUnableToRefresh?: string[];
  openSidepanelTriggeredBy?: string;
  platformAttributes?: PlatformAttributes;
  ssoEnabled: boolean;
  handleLogout: () => void;
  addonDisabledCustomOverlay?: ReactNode;
  setAddonDisabledCustomOverlay: (overlay?: ReactNode) => void;
  isAutoCloneInProgress: boolean;
  isAutoCloneInitialRunCompleted: boolean;
  shouldRefetchAfterAutoCloneSuccess: boolean;
  showMultiUserAccountWarningModal: boolean;
  setShowMultiUserAccountWarningModal: (show: boolean) => void;
}

export type FieldMappabiity =
  | { type: "required" }
  | { type: "optional" }
  | { type: "not_allowed"; reason: string };

export interface CandidateWBMapping {
  checked: boolean;
  columnName: string;
  fieldName?: string;
  fieldLabel?: string;
  fieldType?: string;
}

export interface ActiveSheetInfo {
  fileId?: string;
  excelFileIdentifier?: FileIdentifier;
  fileName?: string;
  tabId?: number;
  excelSheetId?: string;
  tabName?: string;
}

export type WorkbookDataStatus = {
  hasData: boolean;
  sheetCount: number;
  visibleSheetCount: number;
};

export interface WBContext {
  writebackObjectsSchema: WritebackSchema | undefined;
  currentPage: number;
  currentWriteback: Writeback;
  dataSource: ResolvedWritebackDataSource | undefined;
  isExportBlockedForViewer: boolean;
  areFieldMappingsUnavailable: boolean;
  goToAddWritebackConnection: () => void;
  existingDataImport: DataImport | undefined;
  ephemeralError: WritebackError | undefined;
  connectionReauthError: WritebackError | undefined;
  fields: CandidateWBField[];
  autoMappings: CandidateWBMapping[];
  headerError: WritebackError | undefined;
  headerWarning:
    | {
        action?: () => void;
        actionText?: string;
        message?: string;
        messageHtml?: string;
        title: string;
      }
    | undefined;
  headerRowMeta: { startIndex: number; endIndex: number } | undefined;
  mappingError: WritebackError | undefined;
  mappings: CandidateWBMapping[];
  saveWriteback: (mappings?: CandidateWBMapping[]) => void;
  setCurrentWriteback: (writeback: Writeback) => void;
  setEphemeralError: (error?: WritebackError) => void;
  setIdColumn: (id_ss_column_name: string) => void;
  setMappingError: (error?: WritebackError) => void;
  setMappings: (mappings: CandidateWBMapping[]) => void;
  setPage: (index?: number) => void;
  uiStateLoading: boolean;
  schemaLoading: boolean;
  schemaLoadError: WritebackError | undefined;
  writebackTemplateFlow: WritebackTemplateFlow;
  getActiveSheetInfoFromGAS: () => Promise<ActiveSheetInfo>;
  onReauthError: (error: AxiosErrorWithReason) => void;
  refreshMappings: () => Promise<
    | { autoMappings: CandidateWBMapping[]; mappings: CandidateWBMapping[]; writeback: Writeback }
    | undefined
  >;
}

// Note we cannot use the DataObjectType directly on the Writeback UI,
// because lot of the UI has Salesforce hardcoded in it.
// So we need a object & field type similar to SalesforceObjectType && SalesforceObjectTypeField
export interface WritebackObjectType {
  name: string;
  label: string;
  is_featured?: boolean;
  object_type: DataObjectType;
}

export interface WritebackObjectField {
  name?: string;
  label?: string;
  type?: string;
  is_primary_key?: boolean;
  is_external_id?: boolean;
  // True for the per-line key field (e.g. line.line), required for the
  // row→line match in UPDATE_WITH_LINE_ITEMS.
  is_line_key?: boolean;
  is_createable?: boolean;
  is_updateable?: boolean;
  is_nullable?: boolean;
  field: DataObjectField;
}

export type CandidateWBField = WritebackObjectField & {
  field_mappability: FieldMappabiity;
};

export interface StepProps {
  currentIndex?: number;
  stepIndex?: number;
  onNext?: () => void;
  setStep?: (stepIndex: number) => void;
  onSave?: () => void;
  isSaving?: boolean;
  openMessageDialogByDefault?: boolean;
}

export interface Error {
  error: string;
  statusCode: number;
}

export type PPressParamsBase = {
  data_import_name?: string;
  disable_columns_override?: boolean;
  selected_preview_columns?: Column[];
  launch_preview_modal?: boolean;
  flattened_columns?: Array<FlatColumnConfig>;
  default_flattened_column_type?: FlatColumnType;
  static_recipe_key?: string;
};

// TODO: How to expose concrete types of connector params from BE
// so ppress_params can be generically typed?
export type PPressParams = PPressParamsBase & {
  [param: string]: any;
};

export type PPressFormState = {
  is_loading: boolean;
  form_jsx?: string;
  side_pane_form_jsx?: string;
  toolbar_form_jsx?: string;
  no_form_jsx?: boolean;
  refresh_preview_data?: boolean;
  refresh_preview_data_prompt?: boolean;
  show_render_and_refresh_prompt?: boolean;
  display_refresh_button?: boolean;
  fully_valid?: boolean;
  hide_security_footer?: boolean;
  reset_filter_clause?: FilterClause;
};

// Note: Do not use this if ppress_params is already being stored in
// currentDataImport.source_info.ppress_params. It will result in
// duplicate state and can cause issues when trying to keep the two
// in sync.
export type PPressFormStateWithPPressParams<P extends PPressParams = PPressParams> =
  PPressFormState & {
    ppress_params: P;
  };

export type PPressCustomFullScreenProps<P extends PPressParams = PPressParams> = {
  formState: PPressFormStateWithPPressParams<P>;
  metadata: WindowMetadata;
  dataSource?: DataSource;
  dataImport: DataImport;
  apiClient: DefaultApi;
  setDataImport: (dataImport: DataImport) => void;
  onDone: (dataImport: DataImport) => void;
  isLoading: boolean;
  previewError?: unknown;
  setPreviewError: (error?: unknown) => void;
  previewLastImportStatus?: DataImportStatus | undefined;
  onReconnect: () => void;
  closeAndRedirectToAuth: () => void;
  clientFlags?: GetClientFlags200Response;
};

export type PreviewModalProps = {
  user_id?: string | undefined;
  disableFilter?: boolean;
  disableSort?: boolean;
  openModal?: boolean;
  available_objects?: [{ [key: string]: any }];
  selected_object_name?: string;
  filters?: Filter[];
  sorts?: Sort[];
  limit?: number;
  is_gql_mode?: boolean;
  gql_query?: string;
  openPreview?: (trigger: string, props: PreviewModalProps) => void;
  isImportFromDataImportRecipe?: boolean;
  isEditingDataImportRecipe?: boolean;
  dataImportRecipeId?: string;
  openWithGPT?: boolean;
  clientFlags?: GetClientFlags200Response;
  writeback?: Writeback;
  dataSource?: DataSource;
  mappings?: CandidateWBMapping[];
  autoMappings?: CandidateWBMapping[];
  oauthToken?: string;
  egnyteDomain?: string;
};

export type PPressImportDetailProps<P extends PPressParams = PPressParams> = {
  formState: PPressFormStateWithPPressParams<P>;
  formMode: PPressFormMode;
  errorState: ErrorMessageProps;
  setIsCTAHidden: (val: boolean) => void;
  openPreview: (triggerId: string, props?: PreviewModalProps, ppressParams?: PPressParams) => void;
  currentPreviewModalTrigger: string | undefined;
  setIsImportNameHidden: (val: boolean) => void;
  setIsCTADisabled: (val: boolean) => void;
  dataSource: DataSource | undefined;
  dataImport: DataImport;
  setDataImport: (dataImport: DataImport | ((dataImport: DataImport) => DataImport)) => void;
};

export type PPressSidepaneProps<P extends PPressParams = PPressParams> = {
  formState: PPressFormStateWithPPressParams<P>;
  formMode: PPressFormMode;
  availableColumns: PreviewColumn[];
  missingColumns: PreviewColumn[];
  dataSourceType: DataSourceType;
  fetchingPreview: boolean;
  dataReceived: boolean;
  onColumnHover: (column?: PreviewColumn) => void;
  setScrollToRight: () => void;
  dataImport: DataImport;
  setDataImport: (dataImport: DataImport | ((dataImport: DataImport) => DataImport)) => void;
  setFormState: (
    formState: PPressFormState | ((formState: PPressFormState) => PPressFormState)
  ) => void;

  data: Record<string, RunDataImportPreviewRun200ResponsePreviewDataRowsInnerInner>[];
  setData: (
    data: Record<string, RunDataImportPreviewRun200ResponsePreviewDataRowsInnerInner>[]
  ) => void;
  pivotPreviewData: Array<Array<RunDataImportPreviewRun200ResponsePreviewDataRowsInnerInner>>;
  setPivotPreviewData: (
    data: Array<Array<RunDataImportPreviewRun200ResponsePreviewDataRowsInnerInner>>
  ) => void;
  pivotPreviewMetadata: PivotPreviewMetadata | undefined;
  setPivotPreviewMetadata: (data: PivotPreviewMetadata | undefined) => void;
  isLongFetchPreview?: boolean;
  previewError?: unknown;
};

export type PPressToolbarProps<P extends PPressParams = PPressParams> = {
  dataSourceType: DataSourceType;
  formState: PPressFormStateWithPPressParams<P>;
  formMode: PPressFormMode;
  availableColumns: PreviewColumn[];
  missingColumns: PreviewColumn[];
  dataImport: DataImport;
  setDataImport: (dataImport: DataImport | ((dataImport: DataImport) => DataImport)) => void;
  isApplyingTemplate: boolean;
  onApplyTemplate: OnApplyTemplateCb;
  displayTemplatesPopover: boolean;
  eventProps: EventProperties;
  fetchingPreview: boolean;
  fetchPreviewData: (params: {
    dataImport: DataImport;
    triggeredBy: "automatic" | "manual_click";
    ppressParams?: Record<string, any>;
  }) => Promise<RunDataImportPreviewRun200Response | undefined>;
  setPreviewError: (error?: unknown) => void;
};

export type PPressPrimaryContentFormProps<P extends PPressParams = PPressParams> = {
  formState: PPressFormStateWithPPressParams<P>;
  formMode: PPressFormMode;
  dataImport: DataImport;
  setDataImport: (dataImport: DataImport | ((dataImport: DataImport) => DataImport)) => void;
  setFormState: (
    formState: PPressFormState | ((formState: PPressFormState) => PPressFormState)
  ) => void;
};

// ADAPTED FROM https://gist.github.com/fjmorel/e609c087112dfb014a52215458a78c41
// Google Apps Script methods available to scripts
export declare namespace google {
  /**
   * Methods available to Google Apps Script
   */
  namespace script {
    /**
     * google.script.run is an asynchronous client-side JavaScript API available in HTML-service pages that can call server-side Apps Script functions.
     */
    interface IRun {
      // todo myFunction definition
      /**
       * Sets a callback function to run if the server-side function throws an exception. Without a failure handler, failures are logged to the JavaScript console. To override this, call withFailureHandler(null) or supply a failure handler that does nothing.
       * @param callback a client-side callback function to run if the server-side function throws an exception; the Error object is passed to the function as the first argument, and the user object (if any) is passed as a second argument
       */
      withFailureHandler(callback: (error: Error, object?: any) => void): IRun;
      /**
       * Sets a callback function to run if the server-side function returns successfully.
       * @param callback a client-side callback function to run if the server-side function returns successfully; the server's return value is passed to the function as the first argument, and the user object (if any) is passed as a second argument
       */
      withSuccessHandler(callback: (value: any, object?: any) => void): IRun;
      /**
       * Sets an object to pass as a second parameter to the success and failure handlers.
       * @param {Object} object an object to pass as a second parameter to the success and failure handlers; because user objects are not sent to the server, they are not subject to the restrictions on parameters and return values for server calls. User objects cannot, however, be objects constructed with the new operator
       */
      withUserObject(object: object): IRun;
    }
    const run: IRun;
  }
}

export type HubspotObjectTypeFieldWithExtras = HubspotObjectTypeField & {
  parentType?: HubspotObjectTypeFieldType;
  referenced_name?: string;
};

export interface BaseAuthorizeLocationState {
  dataImportRecipeNavigationProps?: DataImportRecipeNavigationProps;
}

export type VCRMessageEnvelope = {
  messageType:
    | "request_data_import"
    | "run_data_import"
    | "data_import"
    | "request_auth_props"
    | "auth_props"
    | "auth_complete"
    | "force_close_session";
  dataImport?: DataImport;
  previewS3File?: string;
  dataSourceType?: DataSourceType;
  authUrl?: string;
  success?: boolean;
  dataSource?: DataSource;
  errorMessage?: string;
};
